import { Alert, Button, Checkbox, Field, Modal, Radio } from '@catering/ui';
import { type FormEvent, type JSX, useState } from 'react';

import type { Contact, Site } from '../../../customers/api';
import {
  PORTAL_ROLE_CODES,
  PORTAL_ROLE_DESCRIPTIONS,
  PORTAL_ROLE_LABELS,
  type InvitePortalAccessInput,
  type PortalRoleCode,
  canOrderForRole,
} from './accesos/api';

export interface PortalAccessInviteModalProps {
  /** Contacts of this customer without a linked portal user yet (`hasPortalUser === false`). */
  contacts: Contact[];
  sites: Site[];
  submitting: boolean;
  formError?: string;
  onCancel: () => void;
  onSubmit: (input: InvitePortalAccessInput) => Promise<void>;
}

/**
 * `portal-access/spec.md` → "Invitación exitosa desde la ficha de
 * cliente". `canOrder` is never a field here: it's derived from `roleCode`
 * (`tasks.md` PR 5, GREEN task 2 — "canOrder implícito del rol").
 */
export function PortalAccessInviteModal({
  contacts,
  sites,
  submitting,
  formError,
  onCancel,
  onSubmit,
}: PortalAccessInviteModalProps): JSX.Element {
  const [contactId, setContactId] = useState('');
  const [roleCode, setRoleCode] = useState<PortalRoleCode | ''>('');
  const [allSites, setAllSites] = useState(true);
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [dailyQuantityLimit, setDailyQuantityLimit] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [touched, setTouched] = useState(false);

  const contactMissing = touched && contactId === '';
  const roleMissing = touched && roleCode === '';
  const validityOutOfOrder = touched && validFrom !== '' && validTo !== '' && validTo < validFrom;

  function toggleSite(siteId: string): void {
    setSiteIds((current) =>
      current.includes(siteId) ? current.filter((id) => id !== siteId) : [...current, siteId],
    );
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setTouched(true);

    if (
      contactId === '' ||
      roleCode === '' ||
      (validFrom !== '' && validTo !== '' && validTo < validFrom)
    ) {
      return;
    }

    void onSubmit({
      contactId,
      roleCode,
      siteIds: allSites ? [] : siteIds,
      canOrder: canOrderForRole(roleCode),
      dailyQuantityLimit: dailyQuantityLimit === '' ? null : Number(dailyQuantityLimit),
      ...(validFrom === '' ? {} : { validFrom }),
      ...(validTo === '' ? {} : { validTo }),
    });
  }

  return (
    <Modal open onClose={onCancel} title="Invitar al portal" className="max-w-lg">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="portal-access-contact" className="text-sm font-medium text-slate-900">
            Contacto
            <span aria-hidden="true" className="ml-0.5 text-red-600">
              *
            </span>
          </label>
          <select
            id="portal-access-contact"
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
            aria-invalid={contactMissing}
            className={`h-11 rounded-md border px-3 text-sm ${contactMissing ? 'border-red-500' : 'border-slate-300'}`}
          >
            <option value="">Seleccioná un contacto</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.fullName}
                {contact.email !== null ? ` (${contact.email})` : ''}
              </option>
            ))}
          </select>
          {contactMissing ? (
            <p role="alert" className="text-xs text-red-600">
              Seleccioná un contacto
            </p>
          ) : null}
          {contacts.length === 0 ? (
            <p className="text-xs text-slate-500">
              Todos los contactos de esta empresa ya tienen un usuario del portal.
            </p>
          ) : null}
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">
            Rol
            <span aria-hidden="true" className="ml-0.5 text-red-600">
              *
            </span>
          </legend>
          {PORTAL_ROLE_CODES.map((code) => (
            <Radio
              key={code}
              name="portal-access-role"
              label={PORTAL_ROLE_LABELS[code]}
              hint={PORTAL_ROLE_DESCRIPTIONS[code]}
              checked={roleCode === code}
              onChange={() => setRoleCode(code)}
            />
          ))}
          {roleMissing ? (
            <p role="alert" className="text-xs text-red-600">
              Seleccioná un rol
            </p>
          ) : null}
        </fieldset>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Sedes</legend>
          <Checkbox
            label="Todas las sedes"
            checked={allSites}
            onChange={(event) => setAllSites(event.target.checked)}
          />
          {!allSites ? (
            <div className="flex flex-col gap-2 pl-6">
              {sites.map((site) => (
                <Checkbox
                  key={site.id}
                  label={site.name}
                  checked={siteIds.includes(site.id)}
                  onChange={() => toggleSite(site.id)}
                />
              ))}
            </div>
          ) : null}
        </fieldset>

        <Field
          label="Tope de cantidad diaria"
          name="dailyQuantityLimit"
          type="number"
          min={0}
          value={dailyQuantityLimit}
          onChange={(event) => setDailyQuantityLimit(event.target.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Vigente desde"
            name="validFrom"
            type="date"
            value={validFrom}
            onChange={(event) => setValidFrom(event.target.value)}
          />
          <Field
            label="Vigente hasta"
            name="validTo"
            type="date"
            value={validTo}
            onChange={(event) => setValidTo(event.target.value)}
          />
        </div>
        {validityOutOfOrder ? (
          <p role="alert" className="text-xs text-red-600">
            La fecha de fin debe ser posterior o igual a la de inicio
          </p>
        ) : null}

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            Invitar
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
